// @flow
import type { EventType } from '~/core/types/beekeeper/actions/events';
import type { Tag } from '~/core/types/beekeeper/actions/tags';
import type { Entitlement } from '~/core/types/beekeeper/entitlements';
import type { AnyIcon } from '~/core/constants/icons';
import type { DeviceType, Region } from '~/core/types/devices';

import { rangeStep } from 'lodash/fp';
import devicesIcons from '~/core/constants/icons/devices-svg';
import generalIcons from '~/core/constants/icons/general-svg';
import weatherIcons from '~/core/constants/icons/weather-svg';
import { deviceTypes, bulbTypes } from '~/core/constants/devices';
import { productTypeToIcon } from '~/core/icons/svg-icons/map';
import { toFarenheit } from '~/core/utils/temperature';
import messages from './intl';

export const actionTypes = {
    MIXED: 'mixed',
    QUICK_ACTION: 'quick-action'
};

export type SettingValue = { value: any, label: string | $npm$ReactIntl$MessageDescriptor };
export type Setting = {
    chooseLabel: $npm$ReactIntl$MessageDescriptor,
    defaultValue: any,
    hide?: boolean, // don't show setting as defined by default (e.g. Dark Outside)
    id: string,
    propertyName: string,
    values: Array<SettingValue>
};

export type SettingsLogic = Array<{
    ifValue?: { [string]: SettingsLogic },
    settingId: string
}>;
export type AttributeDefinition = {
    chooseLabel?: $npm$ReactIntl$MessageDescriptor,
    componentType?: 'checkbox' | 'schedule' | 'select',
    description?: $npm$ReactIntl$MessageDescriptor,
    devices?: Array<DeviceType>,
    entitlements?: Array<Entitlement>,
    hasDuration?: boolean,
    icon: AnyIcon,
    normaliseSettings?: ({ [string]: any }) => { [string]: any },
    processSettings?: ({ [string]: any }) => { [string]: any },
    settings?: {
        definitions: { [string]: Setting },
        logic: SettingsLogic
    },
    text: $npm$ReactIntl$MessageDescriptor,
    type: EventType
};

const getTemperatureList = (farenheit: boolean = false): Array<SettingValue> =>
    rangeStep(0.5, 5, 32.5).map(num => ({ value: num, label: farenheit ? `${toFarenheit(num)}°F` : `${num}°C` }));

const getHumidityList = (): Array<SettingValue> => rangeStep(5, 5, 100).map(num => ({ value: num, label: `${num}%` }));

const listBoostDurations = (): Array<SettingValue> => [
    { value: 1800, label: messages.duration30Minutes },
    { value: 3600, label: messages.duration1Hour },
    { value: 7200, label: messages.duration2Hours },
    { value: 10800, label: messages.duration3Hours },
    { value: 14400, label: messages.duration4Hours },
    { value: 18000, label: messages.duration5Hours },
    { value: 21600, label: messages.duration6Hours }
];

const listSunriseSunsetOffsets = (isSunset): Array<SettingValue> => [
    { value: -3600, label: messages.offset1HourBefore },
    { value: -3000, label: messages.offset50MinutesBefore },
    { value: -2400, label: messages.offset40MinutesBefore },
    { value: -1800, label: messages.offset30MinutesBefore },
    { value: -1200, label: messages.offset20MinutesBefore },
    { value: -600, label: messages.offset10MinutesBefore },
    { value: 0, label: isSunset ? messages.atSunset : messages.atSunrise },
    { value: 600, label: messages.offset10MinutesAfter },
    { value: 1200, label: messages.offset20MinutesAfter },
    { value: 1800, label: messages.offset30MinutesAfter },
    { value: 2400, label: messages.offset40MinutesAfter },
    { value: 3000, label: messages.offset50MinutesAfter },
    { value: 3600, label: messages.offset1HourAfter }
];

const listDarkOutsideOffset = (isSunset: boolean): Array<SettingValue> => {
    const m = isSunset
        ? {
              hourBefore: messages.offset1HourBeforeSunset,
              minutesBefore: messages.offsetMinutesBeforeSunset,
              noOffset: messages.sunset,
              minutesAfter: messages.offsetMinutesAfterSunset,
              hourAfter: messages.offset1HourAfterSunset
          }
        : {
              hourBefore: messages.offset1HourBeforeSunrise,
              minutesBefore: messages.offsetMinutesBeforeSunrise,
              noOffset: messages.sunrise,
              minutesAfter: messages.offsetMinutesAfterSunrise,
              hourAfter: messages.offset1HourAfterSunrise
          };
    return [
        { value: -3600, label: m.hourBefore },
        ...rangeStep(600, -3000, 0).map(num => ({
            value: num,
            label: { ...m.minutesBefore, values: { numMinutes: Math.abs(num) / 60 } }
        })),
        { value: 0, label: m.noOffset },
        ...rangeStep(600, 600, 3600).map(num => ({
            value: num,
            label: { ...m.minutesAfter, values: { numMinutes: Math.abs(num) / 60 } }
        })),
        { value: 3600, label: m.hourAfter }
    ];
};

const listBrightness = (): Array<SettingValue> =>
    rangeStep(10, 10, 110).reduce((acc, value) => [...acc, { value, label: `${value}%` }], [
        { value: -1, label: 'Last used brightness' },
        { value: 5, label: '5%' }
    ]);

export const getAttributeDefinition = (type: EventType, region: Region): AttributeDefinition => {
    const map = {
        'contact-sensor': {
            chooseLabel: messages.chooseSensor,
            devices: [deviceTypes.CONTACT_SENSOR],
            icon: devicesIcons.DOOR_SENSOR,
            settings: {
                definitions: {
                    'contact-sensor-event': {
                        chooseLabel: messages.whenSensor,
                        defaultValue: 'OPEN',
                        id: 'contact-sensor-event',
                        propertyName: 'event',
                        values: [
                            { value: 'OPEN', label: messages.constactSensorOpens },
                            { value: 'CLOSED', label: messages.constactSensorCloses }
                        ]
                    }
                },
                logic: [{ settingId: 'contact-sensor-event' }]
            },
            text: messages.winDoorAttribute
        },
        heating: {
            chooseLabel: messages.heating,
            devices: [deviceTypes.HEATING],
            icon: devicesIcons.HEATING,
            settings: {
                definitions: {
                    mode: {
                        chooseLabel: messages.setTo,
                        defaultValue: 'BOOST',
                        id: 'mode',
                        propertyName: 'mode',
                        values: [
                            { value: 'SCHEDULE', label: messages.heatingSchedule },
                            { value: 'MANUAL', label: messages.heatingManual },
                            { value: 'OFF', label: messages.heatingOff },
                            { value: 'BOOST', label: messages.heatingBoost }
                        ]
                    },
                    setTargetTo: {
                        chooseLabel: messages.setTargetTo,
                        defaultValue: 16,
                        id: 'setTargetTo',
                        propertyName: 'target',
                        values: getTemperatureList()
                    },
                    duration: {
                        chooseLabel: messages.forDuration,
                        defaultValue: 3600,
                        id: 'duration',
                        propertyName: 'duration',
                        values: listBoostDurations()
                    }
                },
                logic: [
                    {
                        settingId: 'mode',
                        ifValue: {
                            MANUAL: [{ settingId: 'setTargetTo' }],
                            BOOST: [{ settingId: 'setTargetTo' }, { settingId: 'duration' }]
                        }
                    }
                ]
            },
            text: messages.heatingAttribute
        },
        'hive-camera': {
            chooseLabel: messages.chooseView,
            devices: [deviceTypes.HIVE_CAMERA],
            icon: devicesIcons.CAMERA_OFF,
            settings: {
                definitions: {
                    turnCamera: {
                        chooseLabel: messages.turnView,
                        defaultValue: 'ON',
                        id: 'turnCamera',
                        propertyName: 'status',
                        values: [{ value: 'ON', label: messages.cameraOn }, { value: 'OFF', label: messages.cameraOff }]
                    }
                },
                logic: [{ settingId: 'turnCamera' }]
            },
            text: messages.cameraAttribute
        },
        'hive-camera-sensor': {
            chooseLabel: messages.chooseView,
            devices: [deviceTypes.HIVE_CAMERA],
            icon: devicesIcons.CAMERA_OFF,
            text: messages.cameraAttribute
        },
        hotwater: {
            chooseLabel: messages.hotwaterAttribute,
            devices: [deviceTypes.HOT_WATER],
            icon: devicesIcons.HOT_WATER,
            settings: {
                definitions: {
                    mode: {
                        chooseLabel: messages.setTo,
                        defaultValue: 'BOOST',
                        id: 'mode',
                        propertyName: 'mode',
                        values: [
                            { value: 'SCHEDULE', label: messages.heatingSchedule },
                            { value: 'MANUAL', label: messages.hotWaterManual },
                            { value: 'OFF', label: messages.heatingOff },
                            { value: 'BOOST', label: messages.heatingBoost }
                        ]
                    },
                    duration: {
                        chooseLabel: messages.forDuration,
                        defaultValue: 3600,
                        id: 'duration',
                        propertyName: 'duration',
                        values: listBoostDurations()
                    }
                },
                logic: [
                    {
                        settingId: 'mode',
                        ifValue: {
                            BOOST: [{ settingId: 'duration' }]
                        }
                    }
                ]
            },
            text: messages.hotwaterAttribute
        },
        humidity: {
            chooseLabel: messages.chooseZone,
            devices: [deviceTypes.NA_THERMOSTAT],
            icon: devicesIcons.HUMIDITY,
            settings: {
                definitions: {
                    'humidity-event': {
                        chooseLabel: messages.whenHumidity,
                        defaultValue: 'GT',
                        id: 'humidity-event',
                        propertyName: 'condition',
                        values: [
                            { value: 'LT', label: messages.temperatureBelow },
                            { value: 'GT', label: messages.temperatureAbove }
                        ]
                    },
                    'humidity-below': {
                        chooseLabel: messages.temperatureBelow,
                        defaultValue: 30,
                        id: 'humidity-below',
                        propertyName: 'humidity',
                        values: getHumidityList()
                    },
                    'humidity-above': {
                        chooseLabel: messages.temperatureAbove,
                        defaultValue: 60,
                        id: 'humidity-above',
                        propertyName: 'humidity',
                        values: getHumidityList()
                    }
                },
                logic: [
                    {
                        settingId: 'humidity-event',
                        ifValue: {
                            GT: [{ settingId: 'humidity-above' }],
                            LT: [{ settingId: 'humidity-below' }]
                        }
                    }
                ]
            },
            text: messages.humidityAttribute
        },
        light: {
            devices: bulbTypes,
            hasDuration: true,
            icon: devicesIcons.LIGHT,
            normaliseSettings: ({ brightness, ...restSettings }) =>
                brightness === -1 ? restSettings : { brightness, ...restSettings },
            settings: {
                definitions: {
                    'light-status': {
                        chooseLabel: messages.turnLight,
                        defaultValue: 'ON',
                        id: 'light-status',
                        propertyName: 'status',
                        values: [{ value: 'ON', label: messages.lightOn }, { value: 'OFF', label: messages.lightOff }]
                    },
                    'at-brightness': {
                        chooseLabel: messages.atBrightness,
                        defaultValue: -1,
                        id: 'at-brightness',
                        propertyName: 'brightness',
                        values: listBrightness()
                    }
                },
                logic: [
                    {
                        settingId: 'light-status',
                        ifValue: {
                            ON: [{ settingId: 'at-brightness' }]
                        }
                    }
                ]
            },
            text: messages.bulbAttribute
        },
        'light-dark': {
            chooseLabel: messages.darkOutside,
            icon: weatherIcons.CLEAR_SKY,
            settings: {
                definitions: {
                    period: {
                        chooseLabel: messages.choosePeriod,
                        defaultValue: 'DARK',
                        hide: true,
                        id: 'period',
                        propertyName: 'period',
                        values: [
                            { value: 'DARK', label: messages.darkOutside },
                            { value: 'LIGHT', label: messages.lightOutside }
                        ]
                    },
                    'sunset-offset': {
                        chooseLabel: messages.from,
                        defaultValue: 0,
                        id: 'sunset-offset',
                        propertyName: 'fromOffset',
                        values: listDarkOutsideOffset(true)
                    },
                    'sunrise-offset': {
                        chooseLabel: messages.until,
                        defaultValue: 0,
                        id: 'sunrise-offset',
                        propertyName: 'untilOffset',
                        values: listDarkOutsideOffset(false)
                    }
                },
                logic: [{ settingId: 'period' }, { settingId: 'sunset-offset' }, { settingId: 'sunrise-offset' }]
            },
            text: messages.darkAttribute
        },
        'motion-sensor': {
            chooseLabel: messages.chooseSensor,
            devices: [deviceTypes.MOTION_SENSOR],
            icon: devicesIcons.MOTION_SENSOR,
            text: messages.motionAttribute
        },
        'na-temperature': {
            chooseLabel: messages.chooseZone,
            devices: [deviceTypes.NA_THERMOSTAT],
            icon: devicesIcons.THERMOSTAT_SLT3,
            settings: {
                definitions: {
                    'temperature-event': {
                        chooseLabel: messages.whenTemperature,
                        defaultValue: 'GT',
                        id: 'temperature-event',
                        propertyName: 'condition',
                        values: [
                            { value: 'LT', label: messages.temperatureBelow },
                            { value: 'GT', label: messages.temperatureAbove }
                        ]
                    },
                    'temperature-below': {
                        chooseLabel: messages.temperatureBelow,
                        defaultValue: 10,
                        id: 'temperature-below',
                        propertyName: 'temperature',
                        values: getTemperatureList(true)
                    },
                    'temperature-above': {
                        chooseLabel: messages.temperatureAbove,
                        defaultValue: 25,
                        id: 'temperature-above',
                        propertyName: 'temperature',
                        values: getTemperatureList(true)
                    }
                },
                logic: [
                    {
                        settingId: 'temperature-event',
                        ifValue: {
                            GT: [{ settingId: 'temperature-above' }],
                            LT: [{ settingId: 'temperature-below' }]
                        }
                    }
                ]
            },
            text: messages.naThermostatAttribute
        },
        nathermostat: {
            icon: devicesIcons.THERMOSTAT_SLT3,
            devices: [deviceTypes.NA_THERMOSTAT],
            processSettings: settings => ({
                ...settings,
                ...(settings.cool ? { quick: 'COOL' } : {}),
                ...(settings.heat ? { quick: 'HEAT' } : {})
            }),
            settings: {
                definitions: {
                    mode: {
                        chooseLabel: messages.setTo,
                        defaultValue: 'BOOST',
                        hide: true,
                        id: 'mode',
                        propertyName: 'mode',
                        values: [{ value: 'BOOST', label: messages.heatingBoost }]
                    },
                    quick: {
                        chooseLabel: messages.setTo,
                        defaultValue: 'HEAT',
                        id: 'quick',
                        propertyName: 'quick',
                        values: [
                            { value: 'COOL', label: messages.quickCool },
                            { value: 'HEAT', label: messages.quickHeat }
                        ]
                    },
                    setHeatTargetTo: {
                        chooseLabel: messages.setTargetTo,
                        defaultValue: 22,
                        id: 'setHeatTargetTo',
                        propertyName: 'heat',
                        values: getTemperatureList(true)
                    },
                    setCoolTargetTo: {
                        chooseLabel: messages.setTargetTo,
                        defaultValue: 24.5,
                        id: 'setCoolTargetTo',
                        propertyName: 'cool',
                        values: getTemperatureList(true)
                    },
                    duration: {
                        chooseLabel: messages.forDuration,
                        defaultValue: 3600,
                        id: 'duration',
                        propertyName: 'duration',
                        values: listBoostDurations()
                    }
                },
                logic: [
                    {
                        settingId: 'mode',
                        ifValue: {
                            BOOST: [
                                {
                                    settingId: 'quick',
                                    ifValue: {
                                        COOL: [{ settingId: 'setCoolTargetTo' }, { settingId: 'duration' }],
                                        HEAT: [{ settingId: 'setHeatTargetTo' }, { settingId: 'duration' }]
                                    }
                                }
                            ]
                        }
                    }
                ]
            },
            text: messages.naThermostatAttribute
        },
        notification: {
            chooseLabel: messages.notifyMe,
            componentType: 'checkbox',
            icon: generalIcons.NOTIFICATIONS,
            settings: {
                definitions: {
                    push: {
                        chooseLabel: messages.notifyPush,
                        defaultValue: true,
                        id: 'push',
                        propertyName: 'push',
                        values: [{ value: true, label: 'true' }, { value: false, label: 'false' }]
                    },
                    email: {
                        chooseLabel: messages.notifyEmail,
                        defaultValue: false,
                        id: 'email',
                        propertyName: 'email',
                        values: [{ value: true, label: 'true' }, { value: false, label: 'false' }]
                    },
                    text: {
                        chooseLabel: messages.notifyText,
                        defaultValue: false,
                        id: 'text',
                        propertyName: 'sms',
                        values: [{ value: true, label: 'true' }, { value: false, label: 'false' }]
                    }
                },
                logic: [
                    { settingId: 'push' },
                    { settingId: 'email' },
                    ...(region === 'UK' ? [{ settingId: 'text' }] : [])
                ]
            },
            text: messages.notifyMe
        },
        plug: {
            chooseLabel: messages.choosePlug,
            devices: [deviceTypes.ACTIVE_PLUG],
            hasDuration: true,
            icon: productTypeToIcon({ localeSuffix: region, type: deviceTypes.ACTIVE_PLUG }),
            settings: {
                definitions: {
                    'plug-status': {
                        chooseLabel: messages.turnPlug,
                        defaultValue: 'ON',
                        id: 'plug-status',
                        propertyName: 'status',
                        values: [{ value: 'ON', label: messages.plugOn }, { value: 'OFF', label: messages.plugOff }]
                    }
                },
                logic: [
                    {
                        settingId: 'plug-status'
                    }
                ]
            },
            text: messages.plugAttribute
        },
        'quick-action': {
            chooseLabel: messages.quickActionChosen,
            description: messages.quickActionDescription,
            icon: generalIcons.DASHBOARD,
            text: messages.quickActionAttribute
        },
        schedule: {
            chooseLabel: messages.betweenTheseTimes,
            componentType: 'schedule',
            icon: generalIcons.SCHEDULE,
            settings: {
                definitions: {
                    schedule: {
                        chooseLabel: messages.betweenTheseTimes,
                        defaultValue: [],
                        id: 'schedule',
                        propertyName: 'schedule',
                        values: []
                    }
                },
                logic: [{ settingId: 'schedule' }]
            },
            text: messages.betweenTheseTimes
        },
        sense: { icon: devicesIcons.SENSE, text: messages.senseAttribute },
        'sunrise-sunset': {
            icon: weatherIcons.SUNSET_SUNRISE,
            settings: {
                definitions: {
                    'sunrise-sunset-event': {
                        chooseLabel: messages.whenSunriseSunset,
                        defaultValue: 'SUNRISE',
                        id: 'sunrise-sunset-event',
                        propertyName: 'event',
                        values: [
                            { value: 'SUNRISE', label: messages.sunrise },
                            { value: 'SUNSET', label: messages.sunset }
                        ]
                    },
                    'sunrise-offset': {
                        chooseLabel: messages.sunrisePreference,
                        defaultValue: 0,
                        id: 'sunrise-offset',
                        propertyName: 'offset',
                        values: listSunriseSunsetOffsets(false)
                    },
                    'sunset-offset': {
                        chooseLabel: messages.sunsetPreference,
                        defaultValue: 0,
                        id: 'sunset-offset',
                        propertyName: 'offset',
                        values: listSunriseSunsetOffsets(true)
                    }
                },
                logic: [
                    {
                        settingId: 'sunrise-sunset-event',
                        ifValue: {
                            SUNRISE: [{ settingId: 'sunrise-offset' }],
                            SUNSET: [{ settingId: 'sunset-offset' }]
                        }
                    }
                ]
            },
            text: messages.sunriseSunsetAttribute
        },
        temperature: {
            chooseLabel: messages.heating,
            devices: [deviceTypes.HEATING],
            icon: devicesIcons.GENERIC_TEMP,
            settings: {
                definitions: {
                    'temperature-event': {
                        chooseLabel: messages.whenTemperature,
                        defaultValue: 'GT',
                        id: 'temperature-event',
                        propertyName: 'condition',
                        values: [
                            { value: 'LT', label: messages.temperatureBelow },
                            { value: 'GT', label: messages.temperatureAbove }
                        ]
                    },
                    'temperature-below': {
                        chooseLabel: messages.temperatureBelow,
                        defaultValue: 10,
                        id: 'temperature-below',
                        propertyName: 'temperature',
                        values: getTemperatureList()
                    },
                    'temperature-above': {
                        chooseLabel: messages.temperatureAbove,
                        defaultValue: 25,
                        id: 'temperature-above',
                        propertyName: 'temperature',
                        values: getTemperatureList()
                    }
                },
                logic: [
                    {
                        settingId: 'temperature-event',
                        ifValue: {
                            GT: [{ settingId: 'temperature-above' }],
                            LT: [{ settingId: 'temperature-below' }]
                        }
                    }
                ]
            },
            text: messages.temperatureAttribute
        }
    };

    return { ...map[type], type };
};

// *** Browse Icons ***

export type CategoryType = 'comfort' | 'efficiency' | 'reassurance';
export type TagDeviceType =
    | 'heating'
    | 'nathermostat'
    | 'light'
    | 'plug'
    | 'motion-sensor'
    | 'contact-sensor'
    | 'quick-action'
    | 'hotwater'
    | 'hive-camera-sensor';

export type FilterIconDefinition<T> = {
    id: T,
    text: $npm$ReactIntl$MessageDescriptor,
    icon: AnyIcon,
    isUniversal: boolean,
    deviceTypes?: Array<string>,
    deviceTypesRequired?: boolean,
    canBeUsed?: boolean,
    regions?: Array<Region>,
    tagsAssociated: Array<Tag>
};
export const categories: Array<FilterIconDefinition<CategoryType>> = [
    {
        id: 'comfort',
        text: messages.comfort,
        icon: generalIcons.COMFORT,
        isUniversal: true,
        tagsAssociated: ['comfort']
    },
    {
        id: 'reassurance',
        text: messages.reassurance,
        icon: generalIcons.REASSURANCE,
        isUniversal: true,
        tagsAssociated: ['reassurance']
    },
    {
        id: 'efficiency',
        text: messages.efficiency,
        icon: generalIcons.EFFICIENCY,
        isUniversal: true,
        tagsAssociated: ['efficiency']
    }
];

export const sortFiltersByUserDevices = (
    a: FilterIconDefinition<TagDeviceType>,
    b: FilterIconDefinition<TagDeviceType>
): number => {
    // both actions are related to devices user owns - no need to sort
    if (a.canBeUsed && b.canBeUsed) {
        return 0;
    }
    // action B isn't related to a device the user owns; or action B is universal - A comes before B
    if (a.canBeUsed) {
        return -1;
    }
    // action A isn't related to a device the user owns; or action A is universal - B comes before A
    if (b.canBeUsed) {
        return 1;
    }
    // neither action is related to a device; A and B are universal
    if (a.isUniversal && b.isUniversal) {
        return 0;
    }
    // neither action is related to a device; A is universal - A comes before B
    if (a.isUniversal) {
        return -1;
    }
    // neither action is related to a device; A is universal - A comes before B
    if (b.isUniversal) {
        return 1;
    }
    return 0;
};

const actionFiltersByProductType = (productTypesUserOwns: Array<string>) => ({
    deviceTypes,
    deviceTypesRequired,
    icon,
    id,
    isUniversal,
    tagsAssociated,
    text
}: FilterIconDefinition<TagDeviceType>): FilterIconDefinition<TagDeviceType> => ({
    id,
    text,
    icon,
    isUniversal,
    canBeUsed: deviceTypes && deviceTypes.some(device => productTypesUserOwns.includes(device)),
    hidden: deviceTypes && deviceTypesRequired && !deviceTypes.every(type => productTypesUserOwns.includes(type)),
    tagsAssociated
});

export const getFilterDevicesList = (
    region: Region,
    productTypesUserOwns: Array<string>
): Array<FilterIconDefinition<TagDeviceType>> =>
    [
        {
            id: 'heating',
            text: messages.heating,
            icon: devicesIcons.HEATING,
            isUniversal: false,
            deviceTypes: [
                deviceTypes.BOILER_MODULE,
                deviceTypes.HEATING,
                deviceTypes.THERMOSTAT_UI,
                deviceTypes.THERMOSTAT
            ],
            regions: ['EU', 'UK'],
            tagsAssociated: ['heating']
        },
        {
            id: 'nathermostat',
            text: messages.naThermostat,
            icon: devicesIcons.THERMOSTAT_SLT3,
            isUniversal: false,
            deviceTypes: [deviceTypes.NA_THERMOSTAT],
            regions: ['NA'],
            tagsAssociated: ['nathermostat']
        },
        {
            id: 'hive-camera-sensor',
            text: messages.cameraAttribute,
            icon: devicesIcons.CAMERA_OFF,
            isUniversal: false,
            deviceTypes: [deviceTypes.HIVE_CAMERA],
            tagsAssociated: ['hive-camera', 'hive-camera-sensor']
        },
        {
            id: 'plug',
            text: messages.plug,
            icon: productTypeToIcon({ localeSuffix: region, type: 'activeplug' }),
            isUniversal: false,
            deviceTypes: [deviceTypes.ACTIVE_PLUG],
            tagsAssociated: ['plug']
        },
        {
            id: 'light',
            text: messages.light,
            icon: devicesIcons.LIGHT,
            isUniversal: false,
            deviceTypes: [
                deviceTypes.BULB,
                deviceTypes.COLOR_TUNEABLE_LIGHT,
                deviceTypes.COLOR_LIGHT,
                deviceTypes.TUNEABLE_LIGHT,
                deviceTypes.WARM_WHITE_LIGHT
            ],
            tagsAssociated: ['light']
        },
        {
            id: 'motion-sensor',
            text: messages.motion,
            icon: devicesIcons.MOTION_SENSOR,
            isUniversal: false,
            deviceTypes: [deviceTypes.MOTION_SENSOR],
            tagsAssociated: ['motion-sensor']
        },
        {
            id: 'contact-sensor',
            text: messages.contact,
            icon: devicesIcons.DOOR_SENSOR,
            isUniversal: false,
            deviceTypes: [deviceTypes.CONTACT_SENSOR],
            tagsAssociated: ['contact-sensor']
        },
        {
            id: 'quick-action',
            text: messages.quickAction,
            icon: generalIcons.DASHBOARD,
            isUniversal: true,
            tagsAssociated: ['quick-action']
        },
        {
            id: 'hotwater',
            text: messages.hotwaterAttribute,
            icon: devicesIcons.HOT_WATER,
            isUniversal: false,
            deviceTypes: [deviceTypes.HOT_WATER],
            deviceTypesRequired: true,
            tagsAssociated: ['hotwater']
        }
    ]
        .filter(item => !item.regions || item.regions.includes(region))
        .map(actionFiltersByProductType(productTypesUserOwns))
        .sort(sortFiltersByUserDevices);

export const DEFAULT_TEMPLATE = 'DEFAULT'; // used for actions that can't be linked to a template (e.g. old recipes)



// WEBPACK FOOTER //
// ./src/core/constants/actions/index.js